/**
 * ui.js
 * LocalStorage DB + 3단 데이터 관리(추가/내보내기/삭제) + 물성치 연동
 */

import { initializeVLEData, METHANOL_VLE_DATA } from './eq_line.js';

// 데이터를 저장할 로컬 스토리지 키
const LOCAL_STORAGE_DB_KEY = 'vle_systems_data';

// 전역 변수: 현재 선택된 시스템의 물성치
// 기본값: Methanol-Water 문헌값
let currentSystemProps = {
  LK_Cp: 81.6,      
  LK_Latent: 35270, 
  HK_Cp: 75.3,      
  HK_Latent: 40660  
};

// --- Helper Functions for LocalStorage DB ---
function getLocalSystems() {
  const data = localStorage.getItem(LOCAL_STORAGE_DB_KEY);
  return data ? JSON.parse(data) : [];
}

function saveLocalSystems(systems) {
  localStorage.setItem(LOCAL_STORAGE_DB_KEY, JSON.stringify(systems));
}

// --- Variable Class ---
export class Variable {
  xd; xb; zf; q; r_factor; nm; sc; alpha;
  Cp_LK; Latent_LK; Cp_HK; Latent_HK;

  constructor() {
    //@ts-ignore
    this.xd = parseFloat(document.getElementById('xd').value);
    //@ts-ignore
    this.xb = parseFloat(document.getElementById('xb').value);
    //@ts-ignore
    this.zf = parseFloat(document.getElementById('zf').value);
    //@ts-ignore
    this.q = parseFloat(document.getElementById('q').value);
    //@ts-ignore
    this.r_factor = parseFloat(document.getElementById('r_factor').value);
    //@ts-ignore
    this.nm = parseFloat(document.getElementById('nm').value);
    //@ts-ignore
    this.sc = parseFloat(document.getElementById('sub_cooling').value);
    
    const systemSelect = document.getElementById('system_select');
    //@ts-ignore
    const isIdealMode = systemSelect && systemSelect.value === 'ideal_binary';

    if (isIdealMode) {
      //@ts-ignore
      this.alpha = parseFloat(document.getElementById('alpha_value').value);
    } else {
      this.alpha = null;
    }

    // 전역 변수에서 물성치 가져오기
    this.Cp_LK = currentSystemProps.LK_Cp || 0;
    this.Latent_LK = currentSystemProps.LK_Latent || 0;
    this.Cp_HK = currentSystemProps.HK_Cp || 0;
    this.Latent_HK = currentSystemProps.HK_Latent || 0;

    this.validate();
  }

  validate() {
    const allValues = [this.xd, this.xb, this.zf, this.q, this.r_factor, this.nm, this.sc];
    if (allValues.some((val) => isNaN(val))) {
      alert('유효하지 않은 입력값이 있습니다. 숫자만 입력해주세요.');
      return false;
    }
    if (this.alpha !== null && (isNaN(this.alpha) || this.alpha <= 1.0)) {
      alert('상대휘발도(α)는 1.0보다 커야 합니다.');
      return false;
    }
    return true;
  }
}

// --- DOM Elements ---
const systemSelect = document.getElementById('system_select');
const systemDeleteSelect = document.getElementById('system_for_delete_select');
const systemOutSelect = document.getElementById('system_for_out_select'); // 내보내기용

const deleteVleBtn = document.getElementById('delete-vle-btn');
const outVleBtn = document.getElementById('out-vle-btn'); // 내보내기 버튼
const inputJsonBtn = document.getElementById('input_json_btn'); // 가져오기 버튼

const systemDescription = document.querySelector('.description');
const vleInputModal = document.getElementById('vle-input-modal');
const addVleRowBtn = document.getElementById('add-vle-row-btn');
const vleDataList = document.getElementById('vle-data-list');
const saveVleBtn = document.getElementById('save-vle-btn');
const cancelVleBtn = document.getElementById('cancel-vle-btn');

const dataManagementModal = document.getElementById('data-management-modal');
const manageDataBtn = document.getElementById('manage-vle-data-btn');
const closeManageBtn = document.getElementById('close-data-management-modal-btn');
const addNewVleDataBtn = document.getElementById('add-new-vle-data-btn');
const alphaContainer = document.getElementById('alpha-container');

let customVLEData = [];

// --- Initialization ---
export function setUpUIListeners() {
  fetchAndPopulateSystems();

  if (systemSelect) systemSelect.addEventListener('change', handleSystemChange);
  
  // 데이터 관리 모달 열기
  if (manageDataBtn) {
    manageDataBtn.addEventListener('click', () => {
      fetchAndPopulateSystems();
      dataManagementModal.classList.remove('hidden');
    });
  }
  // 데이터 관리 모달 닫기
  if (closeManageBtn) {
    closeManageBtn.addEventListener('click', () => dataManagementModal.classList.add('hidden'));
  }
  // 직접 입력(Add New) 창 열기
  if (addNewVleDataBtn) {
    addNewVleDataBtn.addEventListener('click', () => {
      dataManagementModal.classList.add('hidden'); // 관리창 닫고
      showVLEModal(); // 입력창 열기
    });
  }

  // 삭제 & 내보내기 & 가져오기 리스너
  if (deleteVleBtn) deleteVleBtn.addEventListener('click', handleDeleteSystem);
  if (outVleBtn) outVleBtn.addEventListener('click', handleExportSystem);
  if (inputJsonBtn) inputJsonBtn.addEventListener('change', handleImportSystem);

  setUpVLEModalManagement();
  setUpDynamicVLEInput();
}

// --- Data Fetching & UI Population ---
function fetchAndPopulateSystems() {
  const systems = getLocalSystems();

  // 1. 메인 선택 드롭다운
  if (systemSelect) {
    //@ts-ignore
    const currentVal = systemSelect.value;
    systemSelect.innerHTML = '';

    const idealOpt = document.createElement('option');
    idealOpt.value = 'ideal_binary';
    idealOpt.textContent = 'Ideal Binary System (Relative Volatility α)';
    systemSelect.appendChild(idealOpt);

    const defaultOpt = document.createElement('option');
    defaultOpt.value = 'methanol_water';
    defaultOpt.textContent = 'Methanol-Water (Default)';
    systemSelect.appendChild(defaultOpt);

    systems.forEach((sys) => {
      const opt = document.createElement('option');
      opt.value = sys.system_id;
      opt.textContent = sys.system_name;
      systemSelect.appendChild(opt);
    });

    // 선택값 복원
    if (currentVal && (currentVal === 'methanol_water' || systems.find((s) => s.system_id === currentVal))) {
      //@ts-ignore
      systemSelect.value = currentVal;
    }
  }

  // 2. 관리 모달 드롭다운들 업데이트
  updateDropdown(systemDeleteSelect, systems, deleteVleBtn, "삭제할 데이터가 없습니다.");
  updateDropdown(systemOutSelect, systems, outVleBtn, "내보낼 데이터가 없습니다.");
}

// 드롭다운 업데이트용 유틸 함수
function updateDropdown(selectEl, systems, btnEl, emptyMsg) {
  if (!selectEl) return;
  selectEl.innerHTML = '';
  if (systems.length === 0) {
    const opt = document.createElement('option');
    opt.textContent = emptyMsg;
    opt.disabled = true;
    selectEl.appendChild(opt);
    if (btnEl) btnEl.disabled = true;
  } else {
    if (btnEl) btnEl.disabled = false;
    systems.forEach((sys) => {
      const opt = document.createElement('option');
      opt.value = sys.system_id;
      opt.textContent = sys.system_name;
      selectEl.appendChild(opt);
    });
  }
}

// --- System Selection & Loading ---
async function handleSystemChange(event) {
  const selectedValue = event.target.value;
  
  if (selectedValue === 'ideal_binary') {
    if (alphaContainer) alphaContainer.classList.remove('hidden');
    if (systemDescription) systemDescription.innerHTML = `<b>System:</b> Ideal Binary<br><b>Method:</b> Relative Volatility (α)`;
    currentSystemProps = { LK_Cp: 0, LK_Latent: 0, HK_Cp: 0, HK_Latent: 0 };
    return;
  }
  
  if (alphaContainer) alphaContainer.classList.add('hidden');

  if (selectedValue === 'methanol_water') {
    initializeVLEData(METHANOL_VLE_DATA);
    customVLEData = METHANOL_VLE_DATA;
    // 기본값 물성치
    currentSystemProps = { LK_Cp: 81.6, LK_Latent: 35270, HK_Cp: 75.3, HK_Latent: 40660 };
    if (systemDescription) systemDescription.innerHTML = `<b>System:</b> Methanol-Water<br><b>Assumptions:</b> Total Condenser, Partial Reboiler`;
  } else {
    loadSystemFromLocal(selectedValue);
  }
}

function loadSystemFromLocal(systemId) {
  try {
    const systems = getLocalSystems();
    const system = systems.find(s => s.system_id === systemId);
    if (!system) throw new Error('Data not found');

    initializeVLEData(system.vle_points);
    customVLEData = system.vle_points;

    // 물성치 로드
    const p = system.props || {};
    currentSystemProps = { LK_Cp: p.LK_Cp||0, LK_Latent: p.LK_Latent||0, HK_Cp: p.HK_Cp||0, HK_Latent: p.HK_Latent||0 };

    if (systemDescription) {
      systemDescription.innerHTML = `<b>System:</b> ${system.system_name}<br><b>Source:</b> ${system.vle_source}<br>
      <span style="font-size:0.85em; color:#555;">(Props Loaded)</span>`;
    }
  } catch (e) {
    console.error(e);
    alert('데이터 로드 실패. 기본값으로 복귀합니다.');
    //@ts-ignore
    if(systemSelect) systemSelect.value = 'methanol_water';
  }
}

// --- Data Saving (Create) ---
function handleVLEDataSave() {
  const rows = vleDataList.querySelectorAll('.vle-row');
  const data = [];
  rows.forEach((row) => {
    //@ts-ignore
    const x = parseFloat(row.querySelector('.vle-x').value);
    //@ts-ignore
    const y = parseFloat(row.querySelector('.vle-y').value);
    data.push({ x, y });
  });

  if (!validateVLEData(data)) return;
  data.sort((a, b) => a.x - b.x);

  //@ts-ignore
  const compA = document.getElementById('comp_name_A').value.trim();
  //@ts-ignore
  const compB = document.getElementById('comp_name_B').value.trim();
  //@ts-ignore
  const source = document.getElementById('vle_source_input').value.trim();
  
  // 물성치 읽기
  //@ts-ignore
  const lkCp = parseFloat(document.getElementById('LK_vle_Cpl_input').value) || 0;
  //@ts-ignore
  const lkLat = parseFloat(document.getElementById('LK_vle_vLatent_heat_input').value) || 0;
  //@ts-ignore
  const hkCp = parseFloat(document.getElementById('HK_vle_Cpl_input').value) || 0;
  //@ts-ignore
  const hkLat = parseFloat(document.getElementById('HK_vle_vLatent_heat_input').value) || 0;

  const systemName = `${compA}-${compB}`;
  const newSystemId = 'loc_' + Date.now() + Math.floor(Math.random() * 1000);

  const newSystem = {
    system_id: newSystemId,
    system_name: systemName,
    component_a: compA,
    component_b: compB,
    vle_points: data,
    vle_source: source,
    props: { LK_Cp: lkCp, LK_Latent: lkLat, HK_Cp: hkCp, HK_Latent: hkLat },
    created_at: new Date().toISOString()
  };

  try {
    const systems = getLocalSystems();
    systems.push(newSystem);
    saveLocalSystems(systems);

    customVLEData = data;
    currentSystemProps = newSystem.props;
    initializeVLEData(customVLEData);

    hideVLEModal();
    alert(`저장 완료! '${systemName}'`);

    fetchAndPopulateSystems();
    if (systemSelect) {
      //@ts-ignore
      systemSelect.value = newSystemId;
      handleSystemChange({ target: { value: newSystemId } });
    }
  } catch (error) {
    console.error(error);
    alert('저장 실패: ' + error.message);
  }
}

// --- Data Export (JSON) ---
function handleExportSystem() {
  //@ts-ignore
  if (!systemOutSelect || !systemOutSelect.value) {
    alert('내보낼 시스템을 선택해주세요.');
    return;
  }
  //@ts-ignore
  const systemId = systemOutSelect.value;
  const systems = getLocalSystems();
  const target = systems.find(s => s.system_id === systemId);

  if (!target) return alert('데이터 없음');

  const jsonStr = JSON.stringify(target, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `${target.system_name}_VLE.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// --- Data Import (JSON) ---
function handleImportSystem(event) {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = function(e) {
    try {
      //@ts-ignore
      const imported = JSON.parse(e.target.result);
      if (!imported.system_name || !imported.vle_points) throw new Error('필수 데이터 누락');

      // ID 충돌 방지용 새 ID 발급
      const newId = 'loc_' + Date.now() + Math.floor(Math.random() * 1000);
      imported.system_id = newId;
      imported.created_at = new Date().toISOString();

      const systems = getLocalSystems();
      systems.push(imported);
      saveLocalSystems(systems);

      alert(`가져오기 완료: ${imported.system_name}`);
      fetchAndPopulateSystems();
      
      // 모달 닫기
      if (dataManagementModal) dataManagementModal.classList.add('hidden');

      // 바로 선택
      if (systemSelect) {
        //@ts-ignore
        systemSelect.value = newId;
        handleSystemChange({ target: { value: newId } });
      }
    } catch (err) {
      alert('Import Error: ' + err.message);
    } finally {
      event.target.value = ''; // 초기화
    }
  };
  reader.readAsText(file);
}

// --- Delete ---
function handleDeleteSystem() {
  //@ts-ignore
  if (!systemDeleteSelect || !systemDeleteSelect.value) return alert('선택 필요');
  
  //@ts-ignore
  const text = systemDeleteSelect.options[systemDeleteSelect.selectedIndex].text;
  if (!confirm(`정말 '${text}' 데이터를 삭제합니까?`)) return;
  //@ts-ignore
  const id = systemDeleteSelect.value;
  const systems = getLocalSystems();
  const newSystems = systems.filter(s => s.system_id !== id);
  saveLocalSystems(newSystems);

  alert('삭제되었습니다.');
  fetchAndPopulateSystems();

  //@ts-ignore
  if (systemSelect && systemSelect.value === id) {
    //@ts-ignore
    systemSelect.value = 'methanol_water';
    handleSystemChange({ target: { value: 'methanol_water' } });
  }
}

// --- UI Helpers ---
function validateVLEData(data) {
  //@ts-ignore
  const compA = document.getElementById('comp_name_A').value;
  //@ts-ignore
  const compB = document.getElementById('comp_name_B').value;
  if (!compA || !compB) { alert('성분명 필수'); return false; }
  if (data.length < 2) { alert('최소 2개의 데이터 필요'); return false; }
  return true;
}

function showVLEModal() {
  if (vleInputModal) {
    vleInputModal.classList.remove('hidden');
    clearVLERows();
    // 인풋 초기화
    const ids = ['comp_name_A', 'comp_name_B', 'vle_source_input', 'LK_vle_Cpl_input', 'LK_vle_vLatent_heat_input', 'HK_vle_Cpl_input', 'HK_vle_vLatent_heat_input'];
    ids.forEach(id => {
      //@ts-ignore
      const el = document.getElementById(id); if(el) el.value = '';
    });
  }
}

function hideVLEModal() {
  if (vleInputModal) vleInputModal.classList.add('hidden');
  if (dataManagementModal) dataManagementModal.classList.remove('hidden');
}

function setUpVLEModalManagement() {
  if (cancelVleBtn) cancelVleBtn.addEventListener('click', hideVLEModal);
  if (saveVleBtn) saveVleBtn.addEventListener('click', handleVLEDataSave);
}

function setUpDynamicVLEInput() {
  if (addVleRowBtn) addVleRowBtn.addEventListener('click', () => addVLEDataRow());
  if (vleDataList) {
    vleDataList.addEventListener('click', (e) => {
      //@ts-ignore
      if (e.target.classList.contains('remove-vle-row')) {
        //@ts-ignore
        e.target.closest('.vle-row').remove();
        updateRemoveButtonVisibility();
      }
    });
  }
}

function addVLEDataRow(x = 0.5, y = 0.5) {
  if (!vleDataList) return;
  const row = document.createElement('div');
  row.className = 'vle-row';
  row.innerHTML = `<label>x:</label><input type="number" step="any" min="0" max="1" value="${x}" class="vle-x"><label>y:</label><input type="number" step="any" min="0" max="1" value="${y}" class="vle-y"><button type="button" class="remove-vle-row">-</button>`;
  vleDataList.appendChild(row);
  updateRemoveButtonVisibility();
}

function clearVLERows() {
  if (!vleDataList) return;
  vleDataList.innerHTML = '';
  // 0,0 및 1,1 기본 추가
  const r1 = document.createElement('div'); r1.className = 'vle-row'; r1.innerHTML = `<label>x:</label><input value="0.0" readonly class="vle-x"><label>y:</label><input value="0.0" readonly class="vle-y"><button class="remove-vle-row" style="visibility:hidden">-</button>`;
  vleDataList.appendChild(r1);
  const r2 = document.createElement('div'); r2.className = 'vle-row'; r2.innerHTML = `<label>x:</label><input value="1.0" readonly class="vle-x"><label>y:</label><input value="1.0" readonly class="vle-y"><button class="remove-vle-row" style="visibility:hidden">-</button>`;
  vleDataList.appendChild(r2);
  updateRemoveButtonVisibility();
}

function updateRemoveButtonVisibility() {
  const rows = vleDataList.querySelectorAll('.vle-row');
  rows.forEach((row, idx) => {
    const btn = row.querySelector('.remove-vle-row');
    //@ts-ignore
    if(btn) btn.style.visibility = (idx === 0 || idx === rows.length - 1) ? 'hidden' : 'visible';
  });
}

export function getCustomVLEData() { return customVLEData; }

// Initial Call
setUpUIListeners();